/**
 * Match invoice.CalculateLineTotal: persisted quantities have three decimal
 * places. Multiply integer thousandths before rounding to cents so binary
 * fractions (e.g. 42.3 × 175) cannot move an exact half-cent below the tie.
 */
export function calculateLineTotalCents(quantity: number, unitPriceCents: number): number {
  const thousandths = Math.sign(quantity) * Math.round(Math.abs(quantity) * 1000)
  const total = (thousandths * unitPriceCents) / 1000
  return Math.sign(total) * Math.round(Math.abs(total))
}

export type LineItemTaxRate = {
  id: string
  name: string
  /** Whole-percent rate as authored in settings, e.g. 8.25 for 8.25%. */
  percentage: number
}

export type LineItemTaxFields = {
  isTaxable: boolean
  salesTaxRateId?: string
  salesTaxRateName?: string
  /** Decimal fraction, e.g. 0.0825. */
  salesTaxRate?: number
}

/**
 * Tax fields for a chosen rate, or the cleared shape for "Non-taxable".
 * Single source for every surface that lets a user pick a rate — the row's
 * inline picker, the add-on picker and the edit sheets must agree.
 */
export function resolveLineItemTaxFields(taxRate: LineItemTaxRate | undefined): LineItemTaxFields {
  if (!taxRate) {
    return {
      isTaxable: false,
      salesTaxRateId: undefined,
      salesTaxRateName: undefined,
      salesTaxRate: undefined,
    }
  }

  return {
    isTaxable: true,
    salesTaxRateId: taxRate.id,
    salesTaxRateName: taxRate.name,
    salesTaxRate: taxRate.percentage / 100,
  }
}

/** Applies a chosen rate to a line item, recalculating its sales tax. */
export function applyTaxRateToLineItem<
  T extends {
    quantity: number
    unitPriceCents: number
    isTaxable: boolean
    salesTaxRateId?: string
    salesTaxRateName?: string
    salesTaxRate?: number
    salesTaxCents: number
  },
>(item: T, taxRate: LineItemTaxRate | undefined): T {
  const fields = resolveLineItemTaxFields(taxRate)
  const lineTotalCents = calculateLineTotalCents(item.quantity, item.unitPriceCents)

  return {
    ...item,
    ...fields,
    salesTaxCents: fields.salesTaxRate ? Math.round(lineTotalCents * fields.salesTaxRate) : 0,
  }
}
